import { FIELD_ROW, scopePath } from './scope'
import { transaction } from './transaction'
export { beginOperation } from './transaction'
import type { Block, Blocks, TextSegment } from '@w/content-blocks/src/block'
import type { Edit, Json } from '@w/crdt'
import { indexViewBlocks } from '../doc-blocks'
import { canonicalBlocks } from './canonical-blocks'
import { sliceChars, textChange } from './chars'
import type { CoreState } from './state'
import { children, isAtom, leaf, plain, rowBlock, type Element } from './tree-read'

function location(state: CoreState, block: Block) {
  return indexViewBlocks(transaction(state).read('')).get(block.id)
}

/** Doc path of a text leaf. Before the edit's first op its view is the committed one, whose rows the render already
 * built: a leaf in a row reads its path from them (tree-read `rowBlock`, no page index per keystroke). After an op (a
 * paste's inserts) the view is new, so rows would be a fresh page pass too; the view's index is used, as before. */
function textPath(state: CoreState, block: Block): string | undefined {
  const tx = transaction(state)
  const view = tx.ops.length === 0 ? tx.read('') as { content?: { children?: Blocks } } | undefined : undefined
  return (view?.content?.children && rowBlock(view.content.children, block.id)?.path) ?? location(state, block)?.path
}

function listPath(state: CoreState, items: Blocks): string | undefined {
  const visit = (blocks: Blocks, path: string): string | undefined => {
    if (blocks === items) return path
    for (const block of blocks) {
      for (const [key, value] of Object.entries(block)) {
        if (!Array.isArray(value) || key === 'text') continue
        if (value === items) return `${path}.${block.id}.${key}`
        if (value.every(item => item && typeof item === 'object' && 'type' in item)) {
          const found = visit(value as Blocks, `${path}.${block.id}.${key}`)
          if (found) return found
        }
      }
    }
    return undefined
  }
  const path = state.scope ? scopePath(state.doc, state.scope) : 'content.children'
  return visit(state.scope && state.blocks[0]?.id === FIELD_ROW ? children(state.blocks[0]) : state.blocks, path)
}

function position(state: CoreState, block: Block): { items: Blocks; index: number } | undefined {
  const visit = (items: Blocks): { items: Blocks; index: number } | undefined => {
    const index = items.indexOf(block)
    if (index >= 0) return { items, index }
    for (const item of items) {
      const found = visit(children(item))
      if (found) return found
    }
    return undefined
  }
  return visit(state.blocks)
}

/** Materialize a new template with engine-owned identities, updating the held
 * template references so subsequent commands and selection use the assigned IDs. */
/**
 * Writes a chars block's new `text` as its one changed range, as y-prosemirror's updateYText applies simpleDiff: the
 * retained characters keep their identity, so peers' cursors and anchors in them stay where they are (a whole-value
 * set gives every character a new identity). False when the block holds no text yet.
 */
function editedText(tx: Edit, path: string, value: unknown): boolean {
  const current = tx.read(`${path}.text`)
  if (!Array.isArray(current) || !Array.isArray(value)) return false
  const text = (segments: unknown[]) => segments.map(segment => (segment as TextSegment).text).join('')
  const change = textChange(text(current), text(value))
  if (change.start !== change.end || change.text) tx.editText(`${path}.text`, change.start, change.end - change.start, change.text)
  return true
}

function insertTemplate(tx: Edit, list: string, at: number, block: Block, after?: Block): void {
  const { retained, payload, nested } = templatePayload(tx, list, block)
  let id: string
  if (retained) {
    id = block.id
    const siblings = tx.read(list)
    // Retained descendants already at their destination need no new membership operation.
    if (!Array.isArray(siblings) || (siblings[at] as Block | undefined)?.id !== id) tx.insertRetained(list, at, id)
    const path = `${list}.${id}`
    const old = retained
    for (const key of Object.keys(old)) if (key !== 'id' && !(key in payload)) tx.delete(path, key)
    for (const [key, value] of Object.entries(payload)) {
      if (nested.some(([field]) => key === field)) continue
      if (JSON.stringify(old[key]) === JSON.stringify(value)) continue
      if (key !== 'text' || block.type !== 'chars' || !editedText(tx, path, value)) tx.set(path, key, value)
    }
  } else if (wholeFresh(block)) {
    insertFresh(tx, list, at, [block])
    return
  } else [id] = after ? tx.insertAfter(list, at - 1, [payload]) : tx.insert(list, at, [payload])
  adoptTemplate(tx, list, block, id, nested, !retained)
}

/** New templates side by side in one engine insert, so the list's children are resolved once, not once per block
 * (a 1,000-paragraph paste inserted them one by one). A retained block keeps the one-by-one path. */
function insertTemplates(tx: Edit, list: string, at: number, blocks: Blocks, after: boolean): void {
  if (blocks.every(block => wholeFresh(block))) {
    insertFresh(tx, list, at, blocks)
    return
  }
  if (blocks.some(block => block.id)) {
    for (const [offset, block] of blocks.entries()) insertTemplate(tx, list, at + offset, block, after ? block : undefined)
    return
  }
  const prepared = blocks.map(block => templatePayload(tx, list, block))
  const payloads = prepared.map(each => each.payload)
  const ids = after ? tx.insertAfter(list, at - 1, payloads) : tx.insert(list, at, payloads)
  for (const [offset, block] of blocks.entries()) adoptTemplate(tx, list, block, ids[offset], prepared[offset].nested, true)
}

/** A template with no engine identity anywhere in its subtree and no runtime (`$`) keys: the engine builds it whole
 * (crdt_wasm build_with_id builds nested block lists and slots), so its descendants need no facade calls. A question's
 * `opts` (lists of block lists) is not walked here: its owner takes the per-block path. */
function wholeFresh(block: Block): boolean {
  if (block.id || 'opts' in block) return false
  for (const [key, value] of Object.entries(block)) {
    if (key.startsWith('$')) return false
    if (key !== 'text' && isBlockList(value) && !value.every(child => wholeFresh(child))) return false
  }
  return true
}

type FreshTemplate = { block: Block; payload: Record<string, Json>; nested: [string, Blocks, FreshTemplate[]][] }

/** `templatePayload` of a wholly fresh template with its nested lists filled: each child is normalized against its
 * parent's canonical template, which carries the type and subtype `parentOf` would read back from the engine. */
function freshTemplate(tx: Edit, list: string | undefined, block: Block, parent?: Json): FreshTemplate {
  const fields = canonicalTemplate(tx, list, block, parent)
  const payload: Record<string, Json> = {}
  const nested: [string, Blocks, FreshTemplate[]][] = []
  for (const [key, value] of Object.entries(fields)) {
    if (key === 'id' || value === undefined) continue
    if (Array.isArray(value) && key !== 'text' && isBlockList(value)) {
      const children = value.map(child => freshTemplate(tx, undefined, child, fields as unknown as Json))
      payload[key] = children.map(child => child.payload)
      nested.push([key, value, children])
    } else payload[key] = value as Json
  }
  return { block, payload, nested }
}

/** Wholly fresh templates in one engine insert, each built with its subtree, whose returned ids (every block in
 * document order) give every descendant its engine id (y-prosemirror createTypeFromElementNode builds a subtree before
 * one insert the same way). */
function insertFresh(tx: Edit, list: string, at: number, blocks: Blocks): void {
  const fresh = blocks.map(block => freshTemplate(tx, list, block))
  const built = tx.insertFresh(list, at, fresh.map(each => each.payload))
  for (const [offset, each] of fresh.entries()) {
    const order = built[offset] ?? []
    let next = 0
    adoptFresh(tx, list, each, () => order[next++])
    if (next !== order.length) throw new Error(`Fresh subtree ids do not match its template: ${each.block.type}`)
  }
}

/** Adopts the engine ids in the template's document order (fields in payload order, as the engine builds them). */
function adoptFresh(tx: Edit, list: string, fresh: FreshTemplate, take: () => { id: string; type: string } | undefined): void {
  const { block } = fresh
  const built = take()
  if (!built || built.type !== fresh.payload.type)
    throw new Error(`Fresh subtree ids do not match its template: ${block.type}`)
  const id = built.id
  block.id = id
  const path = `${list}.${id}`
  if (block.type === 'page' && block.pageId === '') {
    block.pageId = id
    tx.set(path, 'pageId', id)
  }
  for (const [key, items, children] of fresh.nested) {
    for (const child of children) adoptFresh(tx, `${path}.${key}`, child, take)
    Reflect.set(block, key, items)
  }
  // The view's text of a chars block is one segment named by the block's id (crdt_wasm block_view), none when empty.
  if (block.type === 'chars') {
    const text = (fresh.payload.text as TextSegment[] | string | undefined) ?? []
    const value = typeof text === 'string' ? text : text.map(segment => segment.text).join('')
    block.text = value ? [{ id, text: value }] : []
  }
}

function templatePayload(tx: Edit, list: string, block: Block) {
  const fields = canonicalTemplate(tx, list, block)
  const retained = (block.id ? tx.readItem(block.id) : undefined) as Record<string, Json> | undefined
  const nested: [string, Blocks][] = []
  const payload: Record<string, Json> = {}
  for (const [key, value] of Object.entries(fields)) {
    if (key === 'id' || value === undefined) continue
    if (Array.isArray(value) && key !== 'text' &&
      (isBlockList(value) || isBlockList(retained?.[key]))) {
      payload[key] = []
      nested.push([key, value as Blocks])
    } else payload[key] = value as Json
  }
  return { retained, payload, nested }
}

function adoptTemplate(tx: Edit, list: string, block: Block, id: string, nested: [string, Blocks][], fresh: boolean): void {
  block.id = id
  const path = `${list}.${id}`
  if (block.type === 'page' && block.pageId === '') {
    block.pageId = id
    tx.set(path, 'pageId', id)
  }
  for (const [key, items] of nested) {
    reconcileChildren(tx, path, key, items, fresh)
    Reflect.set(block, key, items)
  }
  // One node read, not the in-edit view: reading the view re-renders every node this edit touched, so a paste that
  // read it per block was quadratic (docs/benchmarks/sync/editor/README.md, Tried).
  if (block.type === 'chars') block.text = (tx.readItem(id) as { text: TextSegment[] }).text
}

/** A template may change membership intentionally, but moving its owner must not
 * remove and resurrect unchanged children over a concurrent deletion. */
function reconcileChildren(tx: Edit, path: string, key: string, items: Blocks, fresh = false): void {
  const list = `${path}.${key}`
  // A block inserted in this edit was written with this list empty.
  const current = fresh ? [] : tx.read(list)
  if (Array.isArray(current)) {
    const retained = new Set(items.map(item => item.id))
    for (let index = current.length - 1; index >= 0; index -= 1) {
      if (!retained.has((current[index] as Block).id)) tx.remove(list, index, 1)
    }
  } else tx.set(path, key, [])
  if (fresh) insertTemplates(tx, list, 0, items, false)
  else for (const [index, item] of items.entries()) insertTemplate(tx, list, index, item)
}

export function spliceBlocks(
  state: CoreState, items: Blocks, index: number, count: number, ...inserted: Blocks
): Blocks {
  index = index < 0 ? Math.max(0, items.length + index) : Math.min(index, items.length)
  count = Math.max(0, Math.min(count, items.length - index))
  const removed = items.slice(index, index + count)
  const path = listPath(state, items)
  if (path && state.scope && !Array.isArray(transaction(state).read(path))) {
    transaction(state).set(path.slice(0, path.lastIndexOf('.')), path.slice(path.lastIndexOf('.') + 1), [])
  }
  if (state.scope && items === state.blocks && items[0]?.id === FIELD_ROW) {
    const tx = transaction(state)
    const field = scopePath(state.doc, state.scope)
    const current = tx.read(field)
    if (!Array.isArray(current)) tx.set(field.slice(0, field.lastIndexOf('.')), field.slice(field.lastIndexOf('.') + 1), [])
    if (Array.isArray(current) && current.length > 0) tx.remove(field, 0, current.length)
    const next = items.slice()
    next.splice(index, count, ...inserted)
    for (const [offset, block] of next.entries()) {
      if (block.id === FIELD_ROW) block.id = ''
      insertTemplate(tx, field, offset, block)
    }
    items.splice(0, items.length, ...next)
    return removed
  }
  if (path) {
    const tx = transaction(state)
    if (count > 0) tx.remove(path, index, count)
    insertTemplates(tx, path, index, inserted, false)
  }
  items.splice(index, count, ...inserted)
  return removed
}

export function insertBlock(state: CoreState, items: Blocks, index: number, block: Block): void {
  spliceBlocks(state, items, index, 0, block)
}

/** A split continuation is placed relative to its predecessor, so an unseen
 * continuation keeps its ownership and order when that predecessor is moved. */
export function insertBlockAfter(state: CoreState, items: Blocks, previous: Block, block: Block): void {
  const index = items.indexOf(previous) + 1
  const path = listPath(state, items)
  if (!path || index === 0) throw new Error('Missing split predecessor')
  insertTemplate(transaction(state), path, index, block, previous)
  items.splice(index, 0, block)
}

/** `blocks` in order after `previous`, as insertBlockAfter one by one would place them, in one engine insert. */
export function insertBlocksAfter(state: CoreState, items: Blocks, previous: Block, blocks: Blocks): void {
  const index = items.indexOf(previous) + 1
  const path = listPath(state, items)
  if (!path || index === 0) throw new Error('Missing split predecessor')
  insertTemplates(transaction(state), path, index, blocks, true)
  items.splice(index, 0, ...blocks)
}

export function removeBlock(state: CoreState, block: Block): void {
  const found = position(state, block)
  if (found) spliceBlocks(state, found.items, found.index, 1)
}

/** Replaces `block` by its children where it stands (unlink: a link's text stays in place). */
export function unwrapBlock(state: CoreState, block: Block): void {
  const found = position(state, block)
  if (found) spliceBlocks(state, found.items, found.index, 1, ...children(block))
}

export function moveBlock(state: CoreState, block: Block, items: Blocks, index: number): void {
  const source = position(state, block)
  const from = location(state, block)
  const list = listPath(state, items)
  if (!source || !from || !list) return
  transaction(state).moveTo(from.list, from.index, list, index)
  source.items.splice(source.index, 1)
  items.splice(index, 0, block)
}

/** A joined-away container remains distinct from an intentional deletion in the CRDT. */
export function joinBlocks(state: CoreState, destination: Element, source: Element, at = children(destination).length): void {
  const into = listPath(state, children(destination))
  const from = listPath(state, children(source))
  const where = position(state, source)
  if (!into || !from || !where) throw new Error('Missing join container')
  transaction(state).join(into, { from, at })
  children(destination).splice(at, 0, ...children(source))
  children(source).splice(0, children(source).length)
  where.items.splice(where.index, 1)
}

export function joinAdjacentLists(state: CoreState, list: Extract<Block, { type: 'list' }>, items: Blocks) {
  let current = list
  const before = items[items.indexOf(current) - 1]
  if (before?.type === 'list' && before.subtype === current.subtype) {
    joinBlocks(state, before, current)
    current = before
  }
  const after = items[items.indexOf(current) + 1]
  if (after?.type === 'list' && after.subtype === current.subtype) joinBlocks(state, current, after)
  return current
}

export function replaceBlock(state: CoreState, block: Block, replacement: Block): void {
  const found = position(state, block)
  if (!found) return
  if (block.id === replacement.id) {
    const props = Object.fromEntries([...new Set([...Object.keys(block), ...Object.keys(replacement)])]
      .filter(key => key !== 'id').map(key => [key, Reflect.get(replacement, key)]))
    setBlockProps(state, block, props)
    Object.assign(replacement, block)
    found.items[found.index] = replacement
  } else spliceBlocks(state, found.items, found.index, 1, replacement)
}

export function setBlockProps(state: CoreState, block: Block, props: Record<string, unknown>): void {
  if (state.scope && block.id === FIELD_ROW) {
    if (Object.keys(props).every(key => key === 'children') && Array.isArray(props.children)) {
      spliceBlocks(state, children(block), 0, children(block).length, ...props.children as Blocks)
    } else {
      Object.assign(block, props)
      spliceBlocks(state, state.blocks, 0, 1, block)
    }
    return
  }
  const found = location(state, block)
  const next = canonicalTemplate(transaction(state), found?.list, { ...block, ...props })
  if (found && 'id' in props && props.id !== block.id) {
    const at = position(state, block)
    if (!at) throw new Error('Expected attached block')
    spliceBlocks(state, at.items, at.index, 1, next)
    for (const key of Object.keys(block)) Reflect.deleteProperty(block, key)
    Object.assign(block, next)
    at.items[at.index] = block
    return
  }
  if (found) {
    const tx = transaction(state)
    const lists: [string, Blocks][] = []
    for (const key of new Set([...Object.keys(block), ...Object.keys(next)])) {
      if (key === 'id') continue
      const value: unknown = Reflect.get(next, key)
      if (value === Reflect.get(block, key)) continue
      if (value === undefined) tx.delete(found.path, key)
      // An empty array has no element shape. Use the previous field to distinguish
      // clearing a block list from clearing scalar answers, criteria, tags, etc.
      else if (key === 'text' && next.type === 'chars' && editedText(tx, found.path, value)) continue
      else if (Array.isArray(value) && key !== 'text' && isBlockList(value.length > 0 ? value : Reflect.get(block, key))) {
        lists.push([key, value as Blocks])
      } else tx.set(found.path, key, value as Json)
    }
    // Children are canonicalized against their owner's final kind (a todo list wraps its items).
    for (const [key, value] of lists) reconcileChildren(tx, found.path, key, value)
    const view = tx.read(found.path)
    if (view && typeof view === 'object') Object.assign(next, structuredClone(view))
  }
  for (const key of Object.keys(block)) Reflect.deleteProperty(block, key)
  Object.assign(block, next)
}

function isBlockList(value: unknown): value is Blocks {
  return Array.isArray(value) && value.length > 0 && value.every(item =>
    item && typeof item === 'object' && 'type' in item)
}

export function replaceChildren(state: CoreState, block: Element, content: Blocks): void {
  setBlockProps(state, block, { children: content })
}

export function editText(
  state: CoreState, block: Extract<Block, { type: 'chars' }>,
  offset: number, count: number, text = '',
): void {
  const path = textPath(state, block)
  if (!path) throw new Error('Text edit requires an attached block')
  const tx = transaction(state)
  tx.editText(`${path}.text`, offset, count, text)
  block.text = tx.read(`${path}.text`) as TextSegment[]
}

export function insertChars(
  state: CoreState, items: Blocks, offset: number, text: string, format: number,
): boolean {
  let remaining = offset
  for (const [index, block] of items.entries()) {
    const length = plain(block).length
    if (isAtom(block)) {
      // Text before an atom is a new leaf at its index; after it, the next child owns o.
      if (remaining > 0) {
        remaining -= length
        continue
      }
      spliceBlocks(state, items, index, 0, leaf(text, format))
      return true
    }
    if (remaining <= length) {
      if (block.type === 'chars' && (block.format ?? 0) === format) {
        editText(state, block, remaining, 0, text)
        return true
      }
      if (block.type === 'cloze' && block.children.length === 0) {
        spliceBlocks(state, block.children, 0, 0, { id: '', type: 'chars', text: [{ id: '', text }], ...(format ? { format } : {}) })
        return true
      }
      if ((block.type === 'a' || block.type === 'cloze' && state.selection?.focus.affinity === 'outside') &&
        (remaining === 0 || remaining === length)) return false
      return insertChars(state, children(block), remaining, text, format)
    }
    remaining -= length
  }
  return false
}

/** Delete exactly the covered interval. Whole segments outside it are never emitted. */
export function deleteRange(state: CoreState, items: Blocks, start: number, end: number): void {
  let offset = 0
  const targets = items.slice()
  for (const block of targets) {
    const length = plain(block).length
    const from = Math.max(0, start - offset)
    const to = Math.min(length, end - offset)
    offset += length
    if (from >= to) continue
    if (from === 0 && to === length) removeBlock(state, block)
    else if (block.type === 'chars') editText(state, block, from, to - from)
    else if (block.type === 'text') {
      setBlockProps(state, block, { text: block.text.slice(0, from) + block.text.slice(to) })
    } else deleteRange(state, children(block), from, to)
  }
}

/** Split inline ownership at a known offset; full segments retain their ids. */
export function takeInlineTail(state: CoreState, items: Blocks, offset: number): Blocks {
  let remaining = offset
  for (let index = 0; index < items.length; index += 1) {
    const block = items[index]
    const length = plain(block).length
    if (remaining === 0) return spliceBlocks(state, items, index, items.length - index)
    if (remaining < length) {
      let tail: Block
      if (block.type === 'chars') {
        tail = { ...block, id: '', text: sliceChars(block.text, remaining, length, state) }
        editText(state, block, remaining, length - remaining)
      } else if (block.type === 'text') {
        tail = { ...block, id: '', text: block.text.slice(remaining) }
        setBlockProps(state, block, { text: block.text.slice(0, remaining) })
      } else {
        tail = { ...block, id: '',
          children: takeInlineTail(state, children(block), remaining) } as Block
      }
      return [tail, ...spliceBlocks(state, items, index + 1, items.length - index - 1)]
    }
    remaining -= length
  }
  return []
}

/** The map owning `list`: a block is read as one node (readItem), the page content needs no read. */
function parentOf(tx: Edit, list: string): Json | undefined {
  const path = list.slice(0, list.lastIndexOf('.'))
  if (path === 'content') return undefined
  return tx.readItem(path.slice(path.lastIndexOf('.') + 1)) ?? tx.read(path)
}

function canonicalTemplate(tx: Edit, list: string | undefined, block: Block, known?: Json): Block {
  // Retained scalar text keeps its storage identity until a text edit replaces it.
  // Moving an ancestor must not author a conversion over a peer's text edit.
  if (block.type === 'text' && block.id && tx.readItem(block.id)) return block
  const parent = known ?? (list ? parentOf(tx, list) : undefined)
  let next = canonicalBlocks([block])[0]
  if (parent && typeof parent === 'object' && !Array.isArray(parent) &&
    parent.type === 'list' && parent.subtype === 'todo') {
    const wrapper = canonicalBlocks([{ id: '', type: 'list', subtype: 'todo', children: [block] }])[0]
    if (wrapper.type === 'list') next = wrapper.children[0]
  }
  // Each nested template is normalized when attached, retaining the caller's
  // references so assigned identities reach the selection and later commands.
  for (const [key, value] of Object.entries(block)) {
    if (isBlockList(value)) {
      if (next === block) continue
      Reflect.set(next, key, value)
    }
  }
  return next
}
